import type { EventStatus } from '../types'

// Saved filter sets for the Events list (item 16). A "set" is a named snapshot
// of the list's filter criteria; one set can be marked the default, which is
// applied automatically when the list first loads.
//
// Persisted in localStorage, keyed per user, so it needs no backend or
// migration. It's a personal convenience — if cross-device sync is wanted
// later this can move server-side without changing the call sites.

export type EventViewMode = 'grid' | 'list'

export interface SavedFilterSet {
  id: string
  name: string
  statusFilter: 'All' | EventStatus
  view: EventViewMode
  showArchived: boolean
  typeIds: number[]
  managerId: number | 'all'
  fromDate: string
  toDate: string
}

export interface FilterSetStore {
  sets: SavedFilterSet[]
  defaultId: string | null
}

const EMPTY: FilterSetStore = { sets: [], defaultId: null }
const KEY = 'nymr-events-filter-sets-v1'

function storageKey(userId: number | null | undefined): string {
  return userId != null ? `${KEY}:${userId}` : KEY
}

export function loadFilterSets(userId: number | null | undefined): FilterSetStore {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return EMPTY
    const parsed = JSON.parse(raw) as Partial<FilterSetStore>
    if (!parsed || !Array.isArray(parsed.sets)) return EMPTY
    return { sets: parsed.sets, defaultId: parsed.defaultId ?? null }
  } catch {
    return EMPTY
  }
}

export function saveFilterSets(userId: number | null | undefined, store: FilterSetStore): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(store))
  } catch {
    // Storage disabled or over quota — saved sets are best-effort, so swallow.
  }
}

export function newFilterSetId(): string {
  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`
}
